import { useQuery } from '@tanstack/react-query'
import { Users, User, Plus } from 'lucide-react'
import { groupsQueryOptions, searchUsers } from '@/utils/recipientQueryOptions'
import { MobileStatus } from './MobileStatus'
import type { Group } from '@/types/groupTypes'
import type { UserData } from '@/types/userTypes'
import { cn } from '@/lib/utils'

export type PickerSegment = 'all' | 'groups' | 'people'

interface RecipientPickerProps {
  query: string
  segment: PickerSegment
  setSegment: (s: PickerSegment) => void
  chosenGroupIds: Set<string>
  chosenPersonIds: Set<string>
  onAddGroup: (g: Group) => void
  onAddPerson: (u: UserData) => void
}

// The dropdown under the TO field: add a whole group or a single person from one
// search. Groups come from /listGroups; people from /searchUser (needs a query).
export function RecipientPicker({
  query,
  segment,
  setSegment,
  chosenGroupIds,
  chosenPersonIds,
  onAddGroup,
  onAddPerson,
}: RecipientPickerProps) {
  const q = query.trim().toLowerCase()

  const groupsQ = useQuery({ ...groupsQueryOptions(), enabled: segment !== 'people' })
  const peopleEnabled = segment !== 'groups' && q.length > 0
  const peopleQ = useQuery({
    queryKey: ['searchUsers', q],
    queryFn: () => searchUsers(q),
    enabled: peopleEnabled,
    staleTime: 30000,
  })

  const groups =
    segment === 'people'
      ? []
      : (groupsQ.data ?? []).filter(
          (g) => !chosenGroupIds.has(g.id) && (!q || g.displayName.toLowerCase().includes(q)),
        )

  const people =
    segment === 'groups'
      ? []
      : (peopleQ.data ?? []).filter((u) => !chosenPersonIds.has(u.id))

  const showPeopleHint = segment === 'people' && q.length === 0
  const noResults =
    groups.length === 0 && people.length === 0 && !peopleQ.isFetching && !showPeopleHint

  const segClass = (s: PickerSegment) =>
    cn(
      'rounded-lg px-3.5 py-[7px] text-[12.5px] font-bold capitalize transition-colors',
      segment === s ? 'bg-surface text-foreground shadow-wha-sm' : 'text-muted-foreground',
    )

  return (
    <div className="mt-2 overflow-hidden rounded-xl border-[1.5px] border-border bg-surface shadow-wha">
      <div className="flex gap-1.5 border-b border-border-2 bg-surface-2 p-2">
        <button type="button" onClick={() => setSegment('all')} className={segClass('all')}>
          All
        </button>
        <button type="button" onClick={() => setSegment('groups')} className={segClass('groups')}>
          Groups
        </button>
        <button type="button" onClick={() => setSegment('people')} className={segClass('people')}>
          People
        </button>
      </div>

      <div className="max-h-[230px] overflow-y-auto">
        {groups.map((g) => (
          <button
            key={`g-${g.id}`}
            type="button"
            onClick={() => onAddGroup(g)}
            className="flex w-full items-center gap-[11px] border-b border-border-2 px-[13px] py-2.5 text-left hover:bg-surface-2"
          >
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-primary-tint text-primary">
              <Users className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13.5px] font-bold text-foreground">
                {g.displayName}
              </span>
              <span className="block truncate text-[11.5px] text-muted-foreground">
                {g.description || 'Azure AD group'}
              </span>
            </span>
            <Plus className="h-[19px] w-[19px] flex-none text-primary" />
          </button>
        ))}

        {peopleQ.isFetching && (
          <div className="px-[13px] py-3 text-[12.5px] text-faint">Searching…</div>
        )}

        {people.map((u) => (
          <button
            key={`p-${u.id}`}
            type="button"
            onClick={() => onAddPerson(u)}
            className="flex w-full items-center gap-[11px] border-b border-border-2 px-[13px] py-2.5 text-left hover:bg-surface-2"
          >
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-teal-soft text-teal">
              <User className="h-4 w-4" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13.5px] font-bold text-foreground">
                {u.displayName}
              </span>
              <MobileStatus hasMobile={!!u.mobilePhone} className="text-[11.5px]" />
            </span>
            <Plus className="h-[19px] w-[19px] flex-none text-primary" />
          </button>
        ))}

        {showPeopleHint && (
          <div className="px-4 py-4 text-center text-[13px] text-faint">
            Type a name to search people
          </div>
        )}
        {noResults && (
          <div className="px-4 py-4 text-center text-[13px] text-faint">No matches</div>
        )}
      </div>
    </div>
  )
}
